import { ArrowTopRightIcon } from '@radix-ui/react-icons';
import posthog from 'posthog-js';
import { buttonVariants } from '@/components/ui/button';
import { BackgroundBeams } from '@/components/ui/background-beams';
import { Button as MovingBorderButton } from '@/components/ui/moving-border';
import { SparklesCore } from '@/components/ui/sparkles';
import { Link } from 'react-router-dom';
import { BrowserFrame } from '../shared/BrowserFrame';
import { ChatCarousel } from '../chat/ChatCarousel';
import { motion, useReducedMotion } from 'framer-motion';

export const Hero = () => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="relative overflow-hidden py-24 md:py-36">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="aceternity-orb absolute -left-32 top-4 h-[34rem] w-[34rem] opacity-70" />
        <div className="aceternity-orb absolute -right-24 top-48 h-[28rem] w-[28rem] opacity-50" />
        {!prefersReducedMotion && (
          <>
            <BackgroundBeams className="opacity-40 [mask-size:58rem] [mask-position:center_top]" />
            <SparklesCore
              id="hero-sparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.4}
              particleColor="#8b5cf6"
              particleDensity={36}
              speed={1.6}
              className="absolute inset-0 h-full w-full opacity-40"
            />
          </>
        )}
      </div>

      <div className="container relative flex flex-col gap-12" id="hero-section">
        <div className="grid items-center gap-10 md:gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="space-y-5">
              <h1 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[4.5rem]">
                Stop losing customers to{' '}
                <span className="aceternity-gradient-text">slow support replies</span>.
              </h1>

              <p className="max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                ChytBot is an AI chatbot for customer service that answers your customers' questions instantly, 24/7 — conversational AI trained on your own docs, prices, and policies. Upload your files, paste one line into your website, done. No coding, no AI expertise.
              </p>
            </div>

            <motion.div
              className="flex flex-col gap-3 sm:flex-row"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <MovingBorderButton
                as={Link}
                to="/dashboard"
                onClick={() => posthog.capture('cta_clicked', { location: 'hero', target: 'dashboard' })}
                borderRadius="0.9rem"
                duration={prefersReducedMotion ? 900000 : 3600}
                containerClassName="h-14 w-full min-w-[210px] text-base sm:w-auto"
                borderClassName="bg-[radial-gradient(hsl(var(--primary))_40%,transparent_70%)] opacity-90"
                className="border border-primary/20 bg-primary text-base font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform hover:scale-[1.02] hover:bg-primary/95 active:scale-[0.98]"
              >
                Get Started Free
                <ArrowTopRightIcon className="ml-1 h-4 w-4" />
              </MovingBorderButton>
              <a
                href="#how-it-works"
                className={buttonVariants({
                  variant: 'outline',
                  size: 'lg',
                  className: 'border-primary/20 bg-background/70 px-8 py-6 text-base font-bold shadow-sm shadow-primary/10 backdrop-blur-xl transition-all hover:scale-[1.02] hover:bg-background/90 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
                })}
              >
                See How It Works
              </a>
            </motion.div>

            <p className="text-sm text-muted-foreground">
              Free plan available &middot; Cancel anytime
            </p>
          </motion.div>

          <motion.div
            className="relative flex min-h-[600px] items-start"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="aceternity-surface relative w-full rounded-[1.75rem] border border-primary/15 p-2 backdrop-blur-xl">
              <div className="pointer-events-none absolute -right-5 -top-5 rounded-full border border-primary/20 bg-background/80 px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary shadow-xl shadow-primary/10 backdrop-blur-xl" aria-hidden="true">
                Live AI preview
              </div>
              <BrowserFrame>
                <ChatCarousel />
              </BrowserFrame>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
